import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, FileText, X, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const statusLabels = { draft: 'Taslak', sent: 'Gonderildi', partial: 'Kismi Teslim', received: 'Teslim Alindi', completed: 'Tamamlandi', cancelled: 'Iptal' };
const statusColors = {
  draft: 'bg-[#e8dfd0] text-[#3F3E39]', sent: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  partial: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', received: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  completed: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  cancelled: 'bg-[#7a3535]/10 text-[#7a3535]'
};

export default function PurchaseOrders() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('purchasing', 'delete');
  const { products } = useProducts();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ supplier_id: '', notes: '', items: [] });
  const [loading, setLoading] = useState(false);
  const [silinecek, setSilinecek] = useState(null);

  const siparisleriGetir = () =>
    api.get('/purchase-orders').then(r => setOrders(r.data)).catch(() => {});

  useEffect(() => {
    siparisleriGetir();
    api.get('/suppliers').then(r => setSuppliers(r.data)).catch(() => {});
  }, []);

  // Silme yalnizca taslak siparisler icin. Gonderilmis siparisin tedarikcide
  // kopyasi var ve bekleyen mal kabul kaydi olusmus durumda.
  const siparisiSil = async () => {
    if (!silinecek) return;
    try {
      const r = await api.delete(`/purchase-orders/${silinecek.id}`);
      toast.success(r.data?.talep_geri_alindi
        ? 'Siparis silindi, talep onayli duruma dondu'
        : 'Siparis silindi');
      siparisleriGetir();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Siparis silinemedi');
    }
    setSilinecek(null);
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { product_card_id: '', quantity: '', unit_price: '', unit: 'kg' }]
    }));
  };

  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i][field] = value;
    if (field === 'product_card_id') {
      const p = products.find(x => x.id === value);
      if (p) {
        items[i].unit = p.unit;
        items[i].product_name = p.name;
        if (p.last_purchase_price) items[i].unit_price = p.last_purchase_price.toString();
      }
    }
    setForm(prev => ({ ...prev, items }));
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      await api.post('/purchase-orders', {
        ...form,
        items: form.items.map(i => ({
          product_card_id: i.product_card_id,
          product_name: i.product_name || '',
          quantity: parseFloat(i.quantity), unit: i.unit,
          unit_price: parseFloat(i.unit_price)
        }))
      });
      toast.success('Siparis olusturuldu');
      setShowNew(false);
      api.get('/purchase-orders').then(r => setOrders(r.data));
    } catch (err) { toast.error('Hata olustu'); }
    setLoading(false);
  };

  return (
    <div data-testid="purchase-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Satin Alma Siparisleri</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{orders.length} siparis</p>
        </div>
        <Button onClick={() => setShowNew(true)}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-order-btn">
          <Plus size={16} className="mr-2" /> Yeni Siparis
        </Button>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tedarikci</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-center">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Kalem</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} className="erp-table-row border-[#d0c4b0]">
                <TableCell className="text-sm text-[#3F3E39]">{new Date(o.created_at).toLocaleDateString('tr-TR')}</TableCell>
                <TableCell className="font-medium text-[#1a1916]">{o.supplier_name}</TableCell>
                <TableCell className="text-center">
                  <span className={`status-badge ${statusColors[o.status]}`}>{statusLabels[o.status]}</span>
                </TableCell>
                <TableCell className="text-right text-[#3F3E39]">{o.items?.length || 0}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{o.total_amount?.toFixed(2)} TL</TableCell>
                <TableCell className="text-right">
                  {/* Gönderilmemiş sipariş: düzenlenebilir ve silinebilir */}
                  {o.status === 'draft' && (
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost"
                        onClick={() => navigate(`/erp/purchase-orders/${o.id}/edit`)}
                        className="text-[#3F3E39] hover:bg-[#e8dfd0] text-xs"
                        data-testid={`edit-order-${o.id}`}>
                        <Pencil size={14} className="mr-1" /> Duzenle
                      </Button>
                      {silmeYetkisi && (
                      <Button size="sm" variant="ghost" onClick={() => setSilinecek(o)}
                        className="text-[#7a3535] hover:bg-[#7a3535]/10 text-xs"
                        data-testid={`delete-order-${o.id}`}>
                        <Trash2 size={14} />
                      </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">Siparis bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Siparis</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Tedarikci *</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm(p => ({ ...p, supplier_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="order-supplier-select">
                  <SelectValue placeholder="Tedarikci secin" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-between items-center">
              <Label className="text-[#3F3E39] font-bold">Urunler</Label>
              <Button type="button" size="sm" onClick={addItem} className="bg-[#2e2d29] text-[#f5f0e8]" data-testid="order-add-item">
                <Plus size={14} className="mr-1" /> Ekle
              </Button>
            </div>
            {form.items.map((item, i) => (
              <div key={i} className="grid grid-cols-4 gap-2 items-end">
                <Select value={item.product_card_id} onValueChange={(v) => updateItem(i, 'product_card_id', v)}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid={`order-item-product-${i}`}>
                    <SelectValue placeholder="Urun" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.code} - {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" step="0.01" placeholder="Miktar" value={item.quantity}
                  onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                  className="bg-white border-[#d0c4b0]" data-testid={`order-item-qty-${i}`} />
                <Input type="number" step="0.01" placeholder="Fiyat" value={item.unit_price}
                  onChange={(e) => updateItem(i, 'unit_price', e.target.value)}
                  className="bg-white border-[#d0c4b0]" data-testid={`order-item-price-${i}`} />
                <Button type="button" variant="ghost" size="icon" onClick={() => setForm(p => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}
                  className="text-[#7a3535]"><X size={16} /></Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleCreate} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="save-order-btn">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(silinecek)} onOpenChange={(a) => !a && setSilinecek(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Siparis silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {silinecek && (
                <>
                  {silinecek.order_no || 'Siparis'} ({silinecek.items?.length || 0} kalem,{' '}
                  {silinecek.total_amount?.toFixed(2)} TL) silinecek. Bu islem geri alinamaz.
                  {silinecek.purchase_request_id &&
                    ' Bagli talep onayli duruma geri doner.'}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgec</AlertDialogCancel>
            <AlertDialogAction onClick={siparisiSil}
              className="bg-[#7a3535] hover:bg-[#5e2929]" data-testid="confirm-delete-order">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
